<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useEventListener } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarGroupSeparator from './SidebarGroupSeparator.vue';

import { useSidebarContext } from './provider';
import { useChannelMultiSelect } from './useChannelMultiSelect';

const props = defineProps({
  name: { type: String, required: true },
  isExpanded: { type: Boolean, default: false },
  label: { type: String, required: true },
  icon: { type: [Object, String], required: true },
  children: { type: Array, default: undefined },
  activeChild: { type: Object, default: undefined },
  sortOptions: { type: Array, default: () => [] },
  activeSort: { type: String, default: '' },
  collapsible: { type: Boolean, default: false },
  showTreeLine: { type: Boolean, default: false },
  endTreeLine: { type: Boolean, default: false },
  // Abilita il campo di ricerca + il conteggio (usato dalla sezione Channels quando
  // i canali sono tanti). Additivo: le altre sottosezioni non lo passano.
  searchable: { type: Boolean, default: false },
  // Abilita la multi-selezione dei canali (checkbox + "Vedi N canali insieme").
  // Passato solo dalla sezione Channels; le altre sottosezioni non lo usano.
  multiSelectable: { type: Boolean, default: false },
});

const emit = defineEmits(['update-sort']);

const { isAllowed } = useSidebarContext();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const scrollableContainer = ref(null);
const accountId = useMapGetter('getCurrentAccountId');

const {
  isSelectionMode,
  selectedCount,
  isSelected,
  toggleSelectionMode,
  disableSelectionMode,
} = useChannelMultiSelect();

const minimizedSectionsKey = LOCAL_STORAGE_KEYS.SIDEBAR_MINIMIZED_SECTIONS;

const getMinimizedSections = () => {
  const minimizedSections = LocalStorage.get(minimizedSectionsKey);
  return minimizedSections &&
    typeof minimizedSections === 'object' &&
    !Array.isArray(minimizedSections)
    ? minimizedSections
    : {};
};

const minimizedSections = ref(getMinimizedSections());
const storageKey = computed(() =>
  accountId.value ? `${accountId.value}:${props.name}` : props.name
);
const isSubGroupExpanded = computed(
  () => !props.collapsible || !minimizedSections.value[storageKey.value]
);
const hasActiveChild = computed(() =>
  props.children.some(child => child.name === props.activeChild?.name)
);

const accessibleItems = computed(() =>
  props.children.filter(child => {
    return child.to && isAllowed(child.to);
  })
);

const hasAccessibleItems = computed(() => {
  return accessibleItems.value.length > 0;
});

// Ricerca: sotto questa soglia non serve e ingombrerebbe.
const SEARCHABLE_THRESHOLD = 6;
const searchTerm = ref('');

const showSearch = computed(
  () => props.searchable && accessibleItems.value.length >= SEARCHABLE_THRESHOLD
);

// Conteggio dei canali collegati, mostrato accanto all'etichetta della sezione.
const connectedCount = computed(() =>
  props.searchable ? accessibleItems.value.length : null
);

// I figli effettivamente mostrati: tutti, filtrati per etichetta quando si cerca.
const displayedChildren = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return props.children;

  return props.children.filter(child =>
    (child.label || '').toLowerCase().includes(term)
  );
});

const isScrollable = computed(() => {
  return (
    props.isExpanded &&
    isSubGroupExpanded.value &&
    accessibleItems.value.length > 7
  );
});

const scrollEnd = ref(false);

const CHILDREN_TRUNK =
  "before:content-[''] before:absolute before:top-0 before:bottom-0 before:w-0.5 before:bg-n-slate-4 before:start-[-0.5rem]";

const hideLeafTreeLine = computed(
  () => props.showTreeLine && !props.isExpanded
);

const toggleSubGroup = () => {
  if (!props.collapsible) return;

  if (isSubGroupExpanded.value) {
    LocalStorage.updateJsonStore(minimizedSectionsKey, storageKey.value, true);
  } else {
    LocalStorage.deleteFromJsonStore(minimizedSectionsKey, storageKey.value);
  }

  minimizedSections.value = getMinimizedSections();
};

const expandSubGroupOnActiveChild = () => {
  if (!props.collapsible || !hasActiveChild.value || isSubGroupExpanded.value) {
    return;
  }

  LocalStorage.deleteFromJsonStore(minimizedSectionsKey, storageKey.value);
  minimizedSections.value = getMinimizedSections();
};

const shouldShowItem = child => {
  return (
    isSubGroupExpanded.value &&
    (props.isExpanded || props.activeChild?.name === child.name)
  );
};

// Applica un filtro conversazioni "inbox_id è uno di [selezionati]" riusando
// l'infrastruttura filtri esistente: settiamo il filtro nello store, navighiamo
// alla lista conversazioni e, a navigazione conclusa, notifichiamo ChatList che
// esegue il fetch filtrato (equal_to con più valori => SQL IN sul backend).
const applyChannelFilter = async () => {
  const selectedChannels = props.children.filter(child =>
    isSelected(child.inboxId)
  );
  if (selectedChannels.length < 2) return;

  const payload = [
    {
      attributeKey: 'inbox_id',
      filterOperator: 'equal_to',
      values: selectedChannels.map(child => ({
        id: child.inboxId,
        name: child.label,
      })),
      queryOperator: 'and',
    },
  ];

  disableSelectionMode();
  await router.push(accountScopedRoute('home')).catch(() => {});
  await nextTick();
  emitter.emit(BUS_EVENTS.APPLY_CONVERSATION_FILTER, payload);
};

// set scrollEnd to true when the scroll reaches the end
useEventListener(scrollableContainer, 'scroll', () => {
  const { scrollHeight, scrollTop, clientHeight } = scrollableContainer.value;
  scrollEnd.value = scrollHeight - scrollTop === clientHeight;
});

useEventListener(window, 'storage', event => {
  if (event.key === minimizedSectionsKey) {
    minimizedSections.value = getMinimizedSections();
  }
});

watch([hasActiveChild, storageKey], expandSubGroupOnActiveChild, {
  immediate: true,
});
</script>

<template>
  <li class="group/sidebar-section relative flex flex-col list-none min-w-0">
    <template v-if="hasAccessibleItems">
      <SidebarGroupSeparator
        v-show="isExpanded"
        :label
        :icon
        :collapsible
        :count="connectedCount"
        :is-expanded="isSubGroupExpanded"
        :show-tree-line="showTreeLine"
        :end-tree-line="endTreeLine"
        :sort-options="sortOptions"
        :active-sort="activeSort"
        class="my-1"
        @toggle="toggleSubGroup"
        @update-sort="sortBy => emit('update-sort', sortBy)"
      />
      <div
        v-if="showSearch"
        v-show="isExpanded && isSubGroupExpanded"
        :class="{ 'ms-5': collapsible }"
        class="mb-1 px-2"
      >
        <div class="relative flex items-center">
          <Icon
            icon="i-lucide-search"
            class="absolute size-4 text-n-slate-10 start-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
          />
          <input
            v-model="searchTerm"
            type="search"
            :placeholder="$t('SIDEBAR.SEARCH_CHANNELS')"
            class="reset-base w-full h-8 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12 ps-9 pe-2.5 outline outline-1 outline-n-weak focus:outline-n-brand"
          />
        </div>
      </div>
      <ul
        v-if="children.length"
        class="m-0 list-none reset-base relative group min-w-0"
        :class="[
          { 'ms-5': collapsible },
          showTreeLine && !endTreeLine && CHILDREN_TRUNK,
        ]"
      >
        <div
          ref="scrollableContainer"
          class="min-w-0"
          :class="{
            'max-h-60 overflow-y-scroll no-scrollbar': isScrollable,
          }"
        >
          <SidebarGroupLeaf
            v-for="child in displayedChildren"
            v-show="shouldShowItem(child)"
            v-bind="child"
            :key="child.name"
            :active="activeChild?.name === child.name"
            :hide-tree-line="hideLeafTreeLine"
            thin-tree-line
          />
        </div>
        <div
          v-if="isScrollable && isExpanded"
          v-show="!scrollEnd"
          class="absolute bg-gradient-to-t from-n-background w-full h-12 to-transparent -bottom-1 pointer-events-none flex items-end justify-end px-2 animate-fade-in-up"
        >
          <Icon
            icon="i-woot-chevrons-down"
            class="w-4 h-6 text-n-slate-9 opacity-50 group-hover:opacity-100"
          />
        </div>
      </ul>
      <div
        v-if="multiSelectable"
        v-show="isExpanded && isSubGroupExpanded"
        :class="{ 'ms-5': collapsible }"
        class="mt-1 px-2 flex flex-col gap-1.5"
      >
        <Button
          sm
          ghost
          slate
          class="!h-7 !justify-start !px-2"
          :icon="isSelectionMode ? 'i-lucide-x' : 'i-lucide-list-checks'"
          :label="
            isSelectionMode
              ? $t('SIDEBAR.CHANNEL_MULTISELECT.CANCEL')
              : $t('SIDEBAR.CHANNEL_MULTISELECT.SELECT')
          "
          @click="toggleSelectionMode"
        />
        <Button
          v-if="isSelectionMode && selectedCount >= 2"
          sm
          solid
          blue
          class="!h-8"
          :label="
            $t('SIDEBAR.CHANNEL_MULTISELECT.VIEW_TOGETHER', {
              count: selectedCount,
            })
          "
          @click="applyChannelFilter"
        />
      </div>
    </template>
  </li>
</template>
